import type { HTMLAttributes } from 'react';

import { Show } from '@dvb/ui';
import { cn } from '@heroui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Props extends HTMLAttributes<HTMLDivElement> {
  text?: string;
}

export function AppSpinner({ className, text = undefined }: Props) {
  const { t } = useTranslation();

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center overflow-hidden">
      <div
        className={cn(
          'relative flex size-52 flex-col items-center justify-center',
          className
        )}
      >
        {/* 外圍旋轉圓環 */}
        <motion.div
          animate={{
            rotate: 360,
          }}
          className="border-primary/50 absolute inset-0 m-auto rounded-full border-4 border-dotted"
          transition={{
            rotate: {
              duration: 16,
              ease: 'linear',
              repeat: Infinity,
            },
          }}
        />

        {/* 內部脈動圓環 - 新增反向3D傾斜效果 */}
        <motion.div
          animate={{
            rotateX: [0, -10, 0, 10, 0],
            rotateY: [0, 10, 0, -10, 0],
            scale: [0.8, 1, 0.8],
          }}
          className="border-secondary/40 absolute inset-0 m-auto size-32 rounded-full border-2"
          transition={{
            rotateX: {
              duration: 6,
              ease: 'easeInOut',
              repeat: Infinity,
            },
            rotateY: {
              duration: 6,
              ease: 'easeInOut',
              repeat: Infinity,
            },
            scale: {
              duration: 3,
              ease: 'easeInOut',
              repeat: Infinity,
            },
          }}
        />

        {/* 中心漸變圓 - 新增輕微3D效果 */}
        <motion.div
          animate={{
            rotateZ: [0, 10, 0, -10, 0],
            scale: [1, 1.15, 1],
          }}
          className="from-primary/15 to-secondary/20 absolute inset-0 m-auto size-24 rounded-full bg-gradient-to-br shadow-lg"
          transition={{
            rotateZ: {
              duration: 4,
              ease: 'easeInOut',
              repeat: Infinity,
            },
            scale: {
              duration: 2,
              ease: 'easeInOut',
              repeat: Infinity,
            },
          }}
        />

        {/* 3D旋轉環 */}
        <motion.div
          animate={{
            rotateX: [0, 65, 0],
            rotateY: [0, 45, 0, -45, 0],
            z: [-20, 0, -20],
          }}
          className="border-primary/30 absolute inset-0 m-auto size-36 rounded-full border"
          style={{ transformStyle: 'preserve-3d' }}
          transition={{
            rotateX: {
              duration: 10,
              ease: 'easeInOut',
              repeat: Infinity,
            },
            rotateY: {
              duration: 8,
              ease: 'easeInOut',
              repeat: Infinity,
            },
            z: {
              duration: 5,
              ease: 'easeInOut',
              repeat: Infinity,
            },
          }}
        />

        {/* 文字 */}
        <Show when={!!text}>
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            className="text-foreground/80 absolute -bottom-12 left-1/2 -translate-x-1/2 text-center text-lg font-medium"
            transition={{
              duration: 1.5,
              ease: 'easeInOut',
              repeat: Infinity,
            }}
          >
            {t('loggingIn')}
          </motion.div>
        </Show>
      </div>
    </div>
  );
}
